import { Fragment, useEffect, useRef, useState } from "react";
import { http, toastApiError, getUserLabels } from "../lib/api";
import { recordLink } from "../lib/notify";
import { useNavDraft } from "../lib/navDraft";
import { fmtTL, fmtDate, todayIso, fmtCreator } from "../lib/format";
import { toast } from "../lib/notify";
import { Plus, Pencil, Trash2, Save, CheckCircle2, CreditCard, ChevronDown, ChevronRight, Clock, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { FilterTh, useColFilters, FilterTotal } from "../components/ColFilters";
import AmountInput, { parseTL } from "../components/AmountInput";
import SearchableSelect from "../components/SearchableSelect";
import { useAuth } from "../context/AuthContext";
import { useRecordFlash } from "../lib/flash";
import { readJson, writeJson } from "../lib/storage";
import { CARD_COLS, cls, emptyLine, emptyCard, emptyRegCard, L, periodEnd, paidLabel } from "../components/cards/cardUtils";
import CardHistory from "../components/cards/CardHistory";
import RegCardForm from "../components/cards/RegCardForm";
import StatementLines from "../components/cards/StatementLines";
import { useStatementLines } from "../components/cards/useStatementLines";

const REG_HEADERS = ["Firma", "Banka", "Kart No", "İsim Soyisim", "Son Kullanma", "Hesap Kesim", "Son Ödeme", "Oluşturan", ""];
const AMOUNT_SUMS = [{ key: "amount", label: "Tutar" }];

export default function CreditCards() {
  const { isAdmin } = useAuth();
  const [cards, setCards] = useState([]);
  useRecordFlash([cards.length]);
  const [form, setForm] = useState(emptyCard);
  const [editing, setEditing] = useState(null); // "new" | id
  useNavDraft("kredi-karti", () => (editing ? { editing, form } : null), (d) => { setEditing(d.editing); setForm(d.form); });
  const [userLabels, setUserLabels] = useState({});
  useEffect(() => { getUserLabels().then(setUserLabels); }, []);
  const [banks, setBanks] = useState([]);
  useEffect(() => { http.get("/banks").then((r) => setBanks(r.data)).catch(() => {}); }, []);
  const bankNames = [...new Set(banks.map((b) => b.name).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr"));
  const companyNames = [...new Set(banks.map((b) => b.company).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr"));
  const [regCards, setRegCards] = useState([]);
  const [regForm, setRegForm] = useState(emptyRegCard);
  const [regEditing, setRegEditing] = useState(null); // "new" | id
  const [openCard, setOpenCard] = useState(null);
  const [budgetItems, setBudgetItems] = useState([]);
  useEffect(() => { http.get("/budget-items").then((r) => setBudgetItems(r.data)).catch(() => {}); }, []);
  const loadRegCards = () => http.get("/registered-cards").then((r) => { setRegCards(r.data); http.get("/budget-items").then((b) => setBudgetItems(b.data)).catch(() => {}); }).catch((e) => toastApiError(e));
  useEffect(() => { loadRegCards(); }, []);
  const regBankOptions = regForm.bank && !bankNames.includes(regForm.bank) ? [regForm.bank, ...bankNames] : bankNames;
  const regCompanyOptions = regForm.company && !companyNames.includes(regForm.company) ? [regForm.company, ...companyNames] : companyNames;

  const toolbarRef = useRef(null);
  const addBtnRef = useRef(null);
  const closeRegForm = () => {
    setRegEditing(null); setRegForm(emptyRegCard);
    setTimeout(() => { toolbarRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); addBtnRef.current?.focus(); }, 60);
  };
  const submitRegCard = async (e) => {
    e.preventDefault();
    try {
      const saved = regEditing === "new" ? (await http.post("/registered-cards", regForm)).data : (await http.put(`/registered-cards/${regEditing}`, regForm)).data;
      toast.success("Kart kaydedildi", { link: recordLink(regEditing === "new" ? saved?.id : regEditing) });
      closeRegForm(); loadRegCards();
    } catch (err) { toastApiError(err); }
  };

  const removeRegCard = async (rc) => {
    if (!window.confirm(`"${rc.bank} — ${rc.card_no}" kartı silinsin mi? Silinenler menüsünden geri alabilirsiniz.`)) return;
    try { await http.delete(`/registered-cards/${rc.id}`); toast.success("Silindi"); loadRegCards(); }
    catch (err) { toastApiError(err); }
  };

  const selCard = regCards.find((x) => x.id === form.registered_card_id);
  const cardBudget = selCard?.budget_item_id ? { budget_parent_id: selCard.budget_parent_id || "", budget_item_id: selCard.budget_item_id } : null;
  const parentOpts = budgetItems.filter((b) => !b.parent_id && b.status !== "Pasif").map((b) => ({ value: b.id, label: b.name }));
  const childOpts = (pid) => budgetItems.filter((b) => b.parent_id === pid && b.status !== "Pasif").map((b) => ({ value: b.id, label: b.name }));
  const sl = useStatementLines(cardBudget);
  const { lines, setLines, resetLines } = sl;
  const pickCard = (id) => {
    const rc = regCards.find((x) => x.id === id);
    setForm((f) => ({ ...f, registered_card_id: id, ...(rc ? { company: rc.company, bank: rc.bank, card_no: rc.card_no } : {}) }));
    if (rc?.budget_item_id) setLines((ls) => ls.map((l) => ({ ...l, budget_parent_id: rc.budget_parent_id || "", budget_item_id: rc.budget_item_id })));
  };
  const fileRef = useRef(null);
  // formu kapat ve Kredi Kartları başlığına geri dön — Kaydet, İptal ve ESC aynı yolu izler
  const closeForm = () => {
    setEditing(null); setForm(emptyCard); resetLines();
    setTimeout(() => { toolbarRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); addBtnRef.current?.focus(); }, 60);
  };
  useEffect(() => {
    if (!editing && !regEditing) return;
    const sel = editing ? '[data-testid="card-form"]' : '[data-testid="regcard-form"]';
    const t = setTimeout(() => { const f = document.querySelector(sel); f?.scrollIntoView({ behavior: "smooth", block: "start" }); f?.querySelector("select,input")?.focus(); }, 60);
    const onKey = (e) => {
      if (e.key !== "Escape" || new URLSearchParams(window.location.search).get("kayit") || e.target.closest("[data-searchable]")) return;
      e.preventDefault(); e.stopPropagation();
      if (editing) closeForm(); else closeRegForm();
    };
    window.addEventListener("keydown", onKey, true);
    return () => { clearTimeout(t); window.removeEventListener("keydown", onKey, true); };
  }, [editing, regEditing]); // eslint-disable-line react-hooks/exhaustive-deps
  const [importing, setImporting] = useState(false);
  const importStatement = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post(`/credit-cards/import-preview?registered_card_id=${form.registered_card_id || ""}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const imported = data.lines.map((l) => ({ ...emptyLine(), ...(cardBudget || {}), tx_date: l.tx_date, amount: String(l.amount).replace(".", ","), description: l.description || "" }));
      setLines((ls) => { const kept = ls.filter((l) => parseTL(l.amount) > 0 || (l.description || "").trim()); return [...kept, ...imported]; });
      const extra = [data.bank_detected ? `${data.bank_detected} formatı tanındı` : "", data.skipped_credit ? `${data.skipped_credit} ödeme/iade satırı atlandı` : ""].filter(Boolean).join(", ");
      toast.success(`${data.count} satır okundu — toplam ${fmtTL(data.total)}${extra ? ` (${extra})` : ""}; kontrol edip Kaydet'e basın`);
      if (data.warning) toast.warning(data.warning, { duration: 8000 });
    } catch (e) { toastApiError(e); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const load = () => http.get("/credit-cards").then((r) => setCards(r.data)).catch((e) => toastApiError(e));
  useEffect(() => { load(); }, []);
  const [hf, setHf, visRows, autoOpts] = useColFilters(cards, CARD_COLS);
  const navigate = useNavigate();
  useEffect(() => { const s = readJson("cc-hf"); if (s) setHf(s); }, [setHf]);
  useEffect(() => { writeJson("cc-hf", hf); }, [hf]);
  const goToPaymentPlan = (c) => {
    const rc = regCards.find((x) => x.id === c.registered_card_id) || { statement_day: 0 };
    const pe = periodEnd(rc, c.statement_date || todayIso());
    const kayit = `cc-${c.registered_card_id || c.card_no}-${pe}`;
    const don = encodeURIComponent(window.location.pathname + window.location.search);
    navigate(`/odeme-plani?kayit=${encodeURIComponent(kayit)}&don=${don}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        if (!form.registered_card_id) return toast.error("Kayıtlı kart seçin");
        const filled = lines.filter((l) => parseTL(l.amount) > 0 || (l.description || "").trim());
        if (filled.length === 0) return toast.error("En az bir satıra tutar girin");
        for (let i = 0; i < filled.length; i++) {
          const l = filled[i];
          if (!(parseTL(l.amount) > 0)) return toast.error(`${lines.indexOf(l) + 1}. satır: tutar girin`);
          if (!l.tx_date) return toast.error(`${lines.indexOf(l) + 1}. satır: işlem tarihi girin`);
          if (!!l.budget_parent_id !== !!l.budget_item_id) return toast.error(`${lines.indexOf(l) + 1}. satır: ana ve alt bütçe kalemini birlikte seçin`);
        }
        const { data } = await http.post("/credit-cards/bulk", { registered_card_id: form.registered_card_id, lines: filled.map(({ _k, ...l }) => ({ ...l, amount: parseTL(l.amount) ?? 0 })) });
        toast.success(`${data.count} satır kaydedildi`);
      } else {
        const payload = { ...form, amount: parseTL(form.amount) ?? 0 };
        await http.put(`/credit-cards/${editing}`, payload);
        toast.success("Kayıt güncellendi");
      }
      closeForm(); load();
    } catch (err) { toastApiError(err); }
  };

  const remove = async (c) => {
    if (!window.confirm(`"${c.bank}" kartı silinsin mi? Silinenler menüsünden geri alabilirsiniz.`)) return;
    try { await http.delete(`/credit-cards/${c.id}`); toast.success("Silindi"); load(); }
    catch (err) { toastApiError(err); }
  };

  const total = cards.reduce((s, c) => s + (c.amount || 0), 0);
  const paidTotal = cards.filter((c) => c.paid).reduce((s, c) => s + (c.amount || 0), 0);

  return (
    <div className="space-y-4" data-testid="cards-page">
      <div ref={toolbarRef} className="flex items-center justify-between flex-wrap gap-3 scroll-mt-20" data-testid="cards-toolbar">
        <div className="flex items-center gap-2 flex-wrap">
          <CreditCard className="w-5 h-5 text-amber-600" />
          <h2 className="text-lg font-bold text-slate-900">Kredi Kartları</h2>
          <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-xs font-semibold" data-testid="card-count">{cards.length} kayıt</span>
          <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-bold" data-testid="card-total">Toplam {fmtTL(total)}</span>
          <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-xs font-semibold" data-testid="card-paid-total">Ödenen {fmtTL(paidTotal)}</span>
          <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 text-xs font-semibold" data-testid="card-open-total">Kalan {fmtTL(total - paidTotal)}</span>
        </div>
        {!regEditing && isAdmin && (
          <button onClick={() => { setRegForm(emptyRegCard); setRegEditing("new"); }} data-testid="regcard-add" className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800">
            <Plus className="w-4 h-4" /> Yeni Kart Kaydet
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="regcards-box">
        <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-slate-600" />
            <span className="text-sm font-bold text-slate-900">Kayıtlı Kartlar</span>
            <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-xs font-semibold" data-testid="regcard-count">{regCards.length} kart</span>
          </div>
          {!editing && (
            <button ref={addBtnRef} onClick={() => { setForm(emptyCard); setEditing("new"); }} data-testid="card-add" title="Ekstre formunu açar; Kaydet / İptal / ESC ile buraya dönülür" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-400">
              <Plus className="w-3.5 h-3.5" /> Yeni Kart Ekstresi
            </button>
          )}
        </div>
        {regEditing && (
          <RegCardForm regForm={regForm} setRegForm={setRegForm} companyOptions={regCompanyOptions} bankOptions={regBankOptions} onSubmit={submitRegCard} onCancel={closeRegForm} />
        )}
        <table className="w-full tbl">
          <thead className="bg-slate-50">
            <tr>
              {REG_HEADERS.map((h) => (
                <th key={h} className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {regCards.map((rc) => (
              <Fragment key={rc.id}>
              <tr onClick={() => setOpenCard(openCard === rc.id ? null : rc.id)} className={`border-t border-slate-100 cursor-pointer ${openCard === rc.id ? "bg-amber-50/60" : "hover:bg-slate-50/60"}`} data-testid={`regcard-row-${rc.id}`} title="Geçmiş ekstreleri görmek için tıklayın">
                <td className="px-3 py-2 text-xs">{rc.company || "—"}</td>
                <td className="px-3 py-2 text-xs font-semibold"><span className="inline-flex items-center gap-1">{openCard === rc.id ? <ChevronDown className="w-3 h-3 text-amber-700" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}{rc.bank}</span></td>
                <td className="px-3 py-2 text-xs font-mono">{rc.card_no}</td>
                <td className="px-3 py-2 text-xs">{rc.holder_name || "—"}</td>
                <td className="px-3 py-2 text-xs">{rc.expiry_date || "—"}</td>
                <td className="px-3 py-2 text-xs">{rc.statement_day ? `${rc.statement_day}. Gün` : "—"}</td>
                <td className="px-3 py-2 text-xs">{rc.due_day ? `${rc.due_day}. Gün` : "—"}</td>
                <td className="px-3 py-2 text-[11px] text-slate-500 whitespace-nowrap">{fmtCreator(rc.created_by, rc.created_at, userLabels)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => { setForm(emptyCard); resetLines(); setEditing("new"); pickCard(rc.id); setTimeout(() => document.querySelector("[data-testid=card-form]")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); }} data-testid={`regcard-new-statement-${rc.id}`} title="Bu kart için yeni ekstre gir"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-amber-500 bg-amber-50 text-amber-800 text-[11px] font-semibold hover:bg-amber-100 mr-2"><Plus className="w-3 h-3" /> Yeni Ekstre</button>
                  {isAdmin ? (
                    <>
                      <button onClick={() => { setRegForm({ ...rc }); setRegEditing(rc.id); }} className="text-slate-600 hover:text-slate-900 mr-2" data-testid={`regcard-edit-${rc.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => removeRegCard(rc)} className="text-red-700 hover:text-red-900" data-testid={`regcard-delete-${rc.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </>
                  ) : <span className="text-[10px] text-slate-400" data-testid={`regcard-readonly-${rc.id}`}>sadece yönetici</span>}
                </td>
              </tr>
              {openCard === rc.id && (
                <tr className="border-t border-amber-200 bg-amber-50/30" data-testid={`regcard-history-${rc.id}`}>
                  <td colSpan={9} className="px-4 py-3"><CardHistory card={rc} txs={cards.filter((c) => c.registered_card_id === rc.id)} isAdmin={isAdmin} onChanged={load} /></td>
                </tr>
              )}
              </Fragment>
            ))}
            {regCards.length === 0 && <tr><td colSpan={9} className="py-6 text-center text-sm text-slate-500" data-testid="regcards-empty">Kayıtlı kart yok — "Yeni Kart Kaydet" ile ekleyin; ekstre girişleri bu kartlara yapılır</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <form onSubmit={submit} onKeyDown={(e) => { if (e.key === "Enter" && e.target.tagName !== "BUTTON" && !e.target.closest("[data-line]") && !e.target.closest("[data-searchable]")) e.preventDefault(); }} className="bg-white border border-slate-200 rounded-lg p-4 space-y-4" data-testid="card-form">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="col-span-2 md:col-span-4">
              <L label="Kayıtlı Kart">
                <select required disabled={editing !== "new"} data-testid="card-field-regcard" value={form.registered_card_id} onChange={(e) => pickCard(e.target.value)} className={`${cls} disabled:bg-slate-50`}>
                  <option value="">Kart seçin…</option>
                  {regCards.map((rc) => <option key={rc.id} value={rc.id}>{`${rc.bank} — ${rc.card_no}${rc.holder_name ? ` (${rc.holder_name})` : ""}${rc.company ? ` — ${rc.company}` : ""}`}</option>)}
                </select>
              </L>
              {regCards.length === 0 && <span className="block text-[11px] text-amber-700 mt-1" data-testid="card-nocard-hint">Önce yukarıdaki "Kayıtlı Kartlar" bölümünden kart tanımlayın</span>}
            </div>
            <L label="Firma"><input readOnly data-testid="card-field-company" value={form.company} className={`${cls} bg-slate-50 text-slate-600`} placeholder="kart seçilince gelir" /></L>
            <L label="Banka"><input readOnly data-testid="card-field-bank" value={form.bank} className={`${cls} bg-slate-50 text-slate-600`} placeholder="kart seçilince gelir" /></L>
            <L label="Kart Adı / No"><input readOnly data-testid="card-field-no" value={form.card_no} className={`${cls} bg-slate-50 text-slate-600`} placeholder="kart seçilince gelir" /></L>
          </div>

          {editing === "new" ? (
            <StatementLines sl={sl} cardBudget={cardBudget} parentOpts={parentOpts} childOpts={childOpts} importing={importing} fileRef={fileRef} onImport={importStatement} />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <L label="İşlem Tarihi"><input required type="date" data-testid="card-field-statement" value={form.statement_date} onChange={(e) => setForm({ ...form, statement_date: e.target.value })} className={cls} /></L>
              <L label="Ana Bütçe Kalemi"><SearchableSelect testid="card-field-parent" value={form.line_budget_parent_id} onChange={(v) => setForm({ ...form, line_budget_parent_id: v, line_budget_item_id: "" })} options={parentOpts} placeholder="Ana kalem…" /></L>
              <L label="Alt Bütçe Kalemi"><SearchableSelect testid="card-field-item" disabled={!form.line_budget_parent_id} value={form.line_budget_item_id} onChange={(v) => setForm({ ...form, line_budget_item_id: v })} options={childOpts(form.line_budget_parent_id)} placeholder="Alt kalem…" /></L>
              <L label="Tutar"><AmountInput required testid="card-field-amount" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} className={cls} placeholder="örn. 25.000,00" /></L>
              <L label="Açıklama"><input data-testid="card-field-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={cls} placeholder="opsiyonel" /></L>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={closeForm} data-testid="card-cancel" title="ESC" className="px-3 py-2 text-xs text-slate-600 hover:text-slate-900">İptal <span className="text-[10px] text-slate-400">(ESC)</span></button>
            <button type="submit" data-testid="card-save" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800"><Save className="w-3.5 h-3.5" /> Kaydet</button>
          </div>
        </form>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <FilterTotal rows={visRows} sums={AMOUNT_SUMS} testid="cards-filter-total" />
        <table className="w-full tbl">
          <thead className="bg-slate-50">
            <tr>
              {CARD_COLS.map((c, i) => (
                <th key={c.key} className={`px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold ${c.key === "amount" ? "text-right" : "text-left"}`}>
                  <FilterTh col={c} hf={hf} setHf={setHf} testid={`card-hf-${c.key}`} align={c.key === "amount" ? "right" : "left"} autoOptions={autoOpts[c.key]} />
                </th>
              ))}
              <th className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold text-left">Oluşturan</th>
              <th className="px-3 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {visRows.map((c) => (
              <tr key={c.id} data-flash-id={c.id} className={`border-t border-slate-100 ${c.paid ? "bg-emerald-50/40" : "hover:bg-slate-50/60"}`} data-testid={`card-row-${c.id}`}>
                <td className="px-3 py-2 text-xs">{c.company || "—"}</td>
                <td className="px-3 py-2 text-xs font-semibold">{c.bank}</td>
                <td className="px-3 py-2 text-xs">{c.card_no || "—"}</td>
                <td className="px-3 py-2 text-xs">{fmtDate(c.statement_date)}</td>
                <td className="px-3 py-2 text-xs">{c.line_budget_parent_name || "—"}</td>
                <td className="px-3 py-2 text-xs">{c.line_budget_item_name || "—"}</td>
                <td className="px-3 py-2 text-xs text-right font-semibold" data-testid={`card-amount-${c.id}`}>{fmtTL(c.amount)}</td>
                <td className="px-3 py-2 text-xs text-slate-600 max-w-[220px] truncate" title={c.description || ""}>{c.description || "—"}</td>
                <td className="px-3 py-2 text-xs">
                  <button type="button" onClick={() => goToPaymentPlan(c)} data-testid={`card-status-${c.id}`} title="Ödeme durumu Ödeme Planı'ndan değiştirilir — tıklayınca ilgili ekstre kaydına gider, ESC ile buraya dönersiniz"
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold border transition-colors ${c.paid ? "bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200" : "bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200"}`}>
                    {c.paid ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />} {paidLabel(c)} <ExternalLink className="w-3 h-3 opacity-60" />
                  </button>
                </td>
                <td className="px-3 py-2 text-[11px] text-slate-500 whitespace-nowrap" data-testid={`card-creator-${c.id}`}>{fmtCreator(c.created_by, c.created_at, userLabels)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {isAdmin ? (
                    <>
                      <button onClick={() => { setForm({ ...emptyCard, ...c }); setEditing(c.id); }} className="text-slate-600 hover:text-slate-900 mr-2" data-testid={`card-edit-${c.id}`} title="Düzenle (yönetici)"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => remove(c)} className="text-red-700 hover:text-red-900" data-testid={`card-delete-${c.id}`} title="Sil (yönetici)"><Trash2 className="w-3.5 h-3.5" /></button>
                    </>
                  ) : <span className="text-[10px] text-slate-400" data-testid={`card-readonly-${c.id}`}>sadece yönetici</span>}
                </td>
              </tr>
            ))}
            {visRows.length === 0 && <tr><td colSpan={11} className="py-8 text-center text-sm text-slate-500" data-testid="cards-empty">{cards.length === 0 ? 'Kredi kartı kaydı yok — "Yeni Kart Ekstresi" ile ekleyin' : "Filtrelerle eşleşen kayıt yok"}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
